import React from 'react';
import { Dialog, AppBar, Typography, Toolbar, Button, List, Divider, Box } from '@mui/material';
import { assign, get, uniq } from 'lodash';
import DistanceSliderFilter from './filters/DistanceSliderFilter';
import DaysSelectorFilter from './filters/DaysSelectorFilter';
import DonorReceiverFilter from './filters/DonorReceiverFilter';
import SiteSelectorFilter from './filters/SiteSelectorFilter';
import RescueAvailabilityFilter from './filters/RescueAvailabilityFilter';
import { initialRescuerScheduleFilters } from '../../reducers/ui';
import { RESCUER_SCHEDULE_FILTER } from '../../pages/rescuerDashboard/rescuerDashboardMobile/Schedule/ScheduleMobile';
import useUIFilter from '../../hooks/useUIFilter';
import { getUniqueDonors, getUniqueReceiver } from '../../helpers/RescuesHelper';
import { getUserSitesByRoles } from '../../services/auth';
import { Roles } from '../../models/roles';
import useActiveUser from '../../hooks/useActiveUser';
import RescueSizeFilter from './filters/RescueSizeFilter';
import CitySelectorFilter from './filters/CitySelectorFilter';
import DonorLinkSelectorFilter from './filters/DonorLinkSelectorFilter';
import useHasActiveFeatureFlag from '../../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from '../FeatureFlagWrapper';
import TimeSelectorFilter from './filters/TimeSelectorFilter';

const RescuerScheduleFiltersDialog = ({
  isOpen = false,
  onDialogClose = () => {},
  filteredRows = [],
  availableRescues,
  isClaimRescueDashboard = false,
}) => {
  const activeUser = useActiveUser();
  const { setUIFilter, getUIFilter } = useUIFilter();
  const rescuerScheduleFilters = assign(initialRescuerScheduleFilters, getUIFilter(RESCUER_SCHEDULE_FILTER));
  const sitesWithRescuerRoles = getUserSitesByRoles(activeUser.role_assignments, Roles.Rescuer);

  const uniqueDonors = getUniqueDonors(availableRescues);
  const uniqueReceivers = getUniqueReceiver(availableRescues);
  const donorReceiverList = [...uniqueDonors, ...uniqueReceivers];
  const pickupCities = uniq(availableRescues.map(ar => `${ar.pickup_city}, ${ar.pickup_st}`));
  const dropoffCities = uniq(availableRescues.map(ar => `${ar.dropoff_city}, ${ar.dropoff_st}`));
  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);

  const updateScheduleFilter = (key, value) => {
    setUIFilter(RESCUER_SCHEDULE_FILTER, {
      ...rescuerScheduleFilters,
      [key]: { value: value },
    });
  };

  if (isClaimRescueDashboard) {
    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          width: '100%',
          overflow: 'auto',
          backgroundColor: 'background.default',
          pt: 1,
          pr: 1,
          pb: '58px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }}>
              {`Filters (${filteredRows.length} results)`}
            </Typography>
          </Toolbar>
        </AppBar>

        <List>
          <DistanceSliderFilter
            value={rescuerScheduleFilters.distance.value}
            handleSliderChange={value => updateScheduleFilter('distance', value)}
          />

          <Divider />

          <CitySelectorFilter
            label="Pickup City"
            cities={pickupCities}
            value={get(rescuerScheduleFilters, 'pickupCities.value', [])}
            handleChange={value => updateScheduleFilter('pickupCities', value)}
          />

          <Divider />

          <CitySelectorFilter
            label="Dropoff City"
            cities={dropoffCities}
            value={get(rescuerScheduleFilters, 'dropoffCities.value', [])}
            handleChange={value => updateScheduleFilter('dropoffCities', value)}
          />

          <Divider />

          <DaysSelectorFilter
            value={rescuerScheduleFilters.days.value}
            handleDaysChange={value => updateScheduleFilter('days', value)}
          />
          <Divider />

          <TimeSelectorFilter
            afterValue={get(rescuerScheduleFilters, 'after.value', false)}
            beforeValue={get(rescuerScheduleFilters, 'before.value', false)}
            handleAfterChange={(value) => updateScheduleFilter('after', value)}
            handleBeforeChange={(value) => updateScheduleFilter('before', value)}
          />
          <Divider />
          <DonorReceiverFilter
            options={donorReceiverList}
            value={rescuerScheduleFilters.donorReceiverList.value}
            handleDonorReceiverChange={value => updateScheduleFilter('donorReceiverList', value)}
          />
          <Divider />
          <RescueSizeFilter
            value={rescuerScheduleFilters.rescueSizes.value}
            handleSizesChange={value => updateScheduleFilter('rescueSizes', value)}
          />
          <Divider />
          {sitesWithRescuerRoles.length > 1 && (
            <>
              <SiteSelectorFilter
                sitesWithRescuerRoles={sitesWithRescuerRoles}
                value={get(rescuerScheduleFilters, 'siteIds.value', [])}
                handleSiteIdsChange={value => updateScheduleFilter('siteIds', value)}
              />
              <Divider />
            </>
          )}

          {hasActiveDonorLinkFF && (
            <>
              <DonorLinkSelectorFilter
                value={get(rescuerScheduleFilters, 'donorLink.value', false)}
                handleFilterChange={value => updateScheduleFilter('donorLink', value)}
              />
              <Divider />
            </>
          )}
          <RescueAvailabilityFilter
            value={get(rescuerScheduleFilters, 'showOnlyAvailableRescues.value', false)}
            handleAvailabilityChange={value => updateScheduleFilter('showOnlyAvailableRescues', value)}
          />
        </List>
      </Box>
    );
  }

  return (
    <Dialog fullScreen fullWidth maxWidth="sm" open={isOpen} onClose={onDialogClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }}>
            {`Filters (${filteredRows.length} results)`}
          </Typography>
          <Button autoFocus color="inherit" onClick={onDialogClose}>
            save
          </Button>
        </Toolbar>
      </AppBar>

      <List>
        <DistanceSliderFilter
          value={rescuerScheduleFilters.distance.value}
          handleSliderChange={value => updateScheduleFilter('distance', value)}
        />

        <Divider />

        <CitySelectorFilter
          label="Pickup City"
          cities={pickupCities}
          value={get(rescuerScheduleFilters, 'pickupCities.value', [])}
          handleChange={value => updateScheduleFilter('pickupCities', value)}
        />

        <Divider />

        <CitySelectorFilter
          label="Dropoff City"
          cities={dropoffCities}
          value={get(rescuerScheduleFilters, 'dropoffCities.value', [])}
          handleChange={value => updateScheduleFilter('dropoffCities', value)}
        />

        <Divider />

        <DaysSelectorFilter
          value={rescuerScheduleFilters.days.value}
          handleDaysChange={value => updateScheduleFilter('days', value)}
        />
        <Divider />

        <TimeSelectorFilter
          afterValue={get(rescuerScheduleFilters, 'after.value', false)}
          beforeValue={get(rescuerScheduleFilters, 'before.value', false)}
          handleAfterChange={(value) => updateScheduleFilter('after', value)}
          handleBeforeChange={(value) => updateScheduleFilter('before', value)}
        />
        <Divider />
        <DonorReceiverFilter
          options={donorReceiverList}
          value={rescuerScheduleFilters.donorReceiverList.value}
          handleDonorReceiverChange={value => updateScheduleFilter('donorReceiverList', value)}
        />
        <Divider />
        <RescueSizeFilter
          value={rescuerScheduleFilters.rescueSizes.value}
          handleSizesChange={value => updateScheduleFilter('rescueSizes', value)}
        />
        <Divider />
        {sitesWithRescuerRoles.length > 1 && (
          <>
            <SiteSelectorFilter
              sitesWithRescuerRoles={sitesWithRescuerRoles}
              value={get(rescuerScheduleFilters, 'siteIds.value', [])}
              handleSiteIdsChange={value => updateScheduleFilter('siteIds', value)}
            />
            <Divider />
          </>
        )}

        {hasActiveDonorLinkFF && (
          <>
            <DonorLinkSelectorFilter
              value={get(rescuerScheduleFilters, 'donorLink.value', false)}
              handleFilterChange={value => updateScheduleFilter('donorLink', value)}
            />
            <Divider />
          </>
        )}
        <RescueAvailabilityFilter
          value={get(rescuerScheduleFilters, 'showOnlyAvailableRescues.value', false)}
          handleAvailabilityChange={value => updateScheduleFilter('showOnlyAvailableRescues', value)}
        />
      </List>
    </Dialog>
  );
};

export default RescuerScheduleFiltersDialog;
